<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import Button from '@components/Button.vue';
import Icon from '@components/Icon.vue';
import IconArrowLeft from '@icons/ph_arrow-left.svg';
import IconArrowRight from '@icons/ph_arrow-right.svg';

const list = ref<HTMLDivElement | null>(null);
const listObserver = ref<MutationObserver | null>(null);

const showLeftButton = ref<boolean>(false);
const showRightButton = ref<boolean>(false);

const checkVisibility = (): void => {
  if (!list.value) {
    return;
  }

  const {
    scrollWidth,
    clientWidth,
  } = list.value;

  const hasOverflow = scrollWidth > clientWidth;

  if (!hasOverflow) {
    showLeftButton.value = false;
    showRightButton.value = false;
    return;
  }

  const { scrollLeft } = list.value;

  showLeftButton.value = scrollLeft > 0;
  showRightButton.value = scrollLeft < scrollWidth - clientWidth - 1;
};

const checkScrollPosition = (): void => {
  checkVisibility();
};

const scrollLeft = (): void => {
  list.value?.scrollBy({
    left: -200,
    behavior: 'smooth',
  });
};

const scrollRight = (): void => {
  list.value?.scrollBy({
    left: 200,
    behavior: 'smooth',
  });
};

onMounted(() => {
  if (!list.value) {
    return;
  }

  listObserver.value = new MutationObserver((mutations) => mutations.forEach(() => checkVisibility()));

  listObserver.value.observe(list.value, {
    childList: true,
  });

  checkScrollPosition();

  list.value.addEventListener('scroll', checkScrollPosition);
  window.addEventListener('resize', checkVisibility);
});

onUnmounted(() => {
  list.value?.removeEventListener('scroll', checkScrollPosition);

  window.removeEventListener('resize', checkVisibility);

  listObserver.value?.disconnect();
});
</script>

<template>
  <div class="horizontal-list relative h-fit">
    <Button
      v-show="showLeftButton"
      variant="circle"
      class="horizontal-list__btn left-0"
      @click="scrollLeft"
    >
      <Icon
        :svg="IconArrowLeft"
        width="20"
        height="20"
      />
    </Button>

    <div
      ref="list"
      :class="[
        'overflow-x-auto',
        'flex gap-x-2',
      ]"
    >
      <slot />
    </div>

    <Button
      v-show="showRightButton"
      variant="circle"
      class="horizontal-list__btn right-0"
      @click="scrollRight"
    >
      <Icon
        :svg="IconArrowRight"
        width="20"
        height="20"
      />
    </Button>
  </div>
</template>

<style lang="scss">
.horizontal-list {
  &__btn {
    @apply absolute z-[1] top-1/2 -translate-y-1/2;

    @apply h-8 w-8;

    @apply flex justify-center items-center;
  }
}
</style>
